import React, { useEffect, useId, useRef, useState } from 'react';
import { useScroll } from 'framer-motion';
import ProgramStoryScene from './ProgramStoryScene';
import './InvestorProgramVisual.css';

const motionQuery = '(min-width: 1001px) and (min-height: 700px) and (prefers-reduced-motion: no-preference)';
const chapters = [
  {
    id: 'biology',
    owner: 'Partner biology',
    title: <>Which antibody<br /><em>should advance?</em></>,
    copy: 'Partners bring the biology and the research that came before.',
  },
  {
    id: 'agents',
    owner: 'The Cornucopia harness',
    title: <>Build the<br /><em>evidence.</em></>,
    copy: 'Specialist agents work with the lab’s research history, plan assays and interpret results. Scientists decide what runs and what advances.',
  },
  {
    id: 'medicine',
    owner: 'Our first-program ambition',
    title: <>Develop it.<br /><em>Together.</em></>,
    copy: 'Co-develop a drug program, with a stake agreed around our scientific contribution.',
  },
];

function ScrollScene({ target }) {
  const { scrollYProgress } = useScroll({
    target,
    offset: ['start 0.18', 'end 0.85'],
  });
  return <div className="program-sticky-stage" aria-hidden="true"><ProgramStoryScene progress={scrollYProgress} /></div>;
}

export default function InvestorProgramVisual() {
  const id = useId();
  const story = useRef(null);
  const [scrollMotion, setScrollMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia?.(motionQuery);
    if (!media) return undefined;
    const sync = () => setScrollMotion(media.matches);
    sync();
    media.addEventListener?.('change', sync);
    return () => media.removeEventListener?.('change', sync);
  }, []);

  return (
    <figure
      className="investor-program-visual"
      data-presentation={scrollMotion ? 'scroll' : 'flow'}
      aria-label="Developing a medicine with a partner"
      aria-describedby={id + '-summary'}
    >
      <p className="program-sr-only" id={id + '-summary'}>
        Our proposed co-development approach: partner biology, specialist scientific work
        under scientist direction, and participation agreed around our contribution.
        CCP-001 is our first-program ambition.
      </p>
      <div className="program-scroll-story" ref={story}>
        <div className="program-story-copy">
          {chapters.map((chapter, index) => (
            <section className="program-story-beat" key={chapter.id} aria-labelledby={id + '-' + chapter.id} data-chapter={chapter.id}>
              <div className="program-beat-text">
                <span className="program-owner">{chapter.owner}</span>
                <h3 id={id + '-' + chapter.id}>{chapter.title}</h3>
                <p>{chapter.copy}</p>
                {index === 1 && <p className="program-sr-only">Atlas supports computational biology, Orin assay design, and Sigma statistical interpretation.</p>}
                {index === 2 && <p className="program-sr-only">Toward CCP-001, developed with a partner.</p>}
              </div>
              {!scrollMotion && <div className="program-flow-scene" aria-hidden="true"><ProgramStoryScene chapter={index} /></div>}
            </section>
          ))}
        </div>
        {scrollMotion && <ScrollScene target={story} />}
      </div>
    </figure>
  );
}
